import { parseFiltersFromQS } from '@repo-level/core-front';
import type { BonusI } from 'api/cms/bonuses';
import { decrypt } from 'helpers/common';
import { LocalStorage } from 'helpers/localStorage';
import { Maybe } from 'monet';
import { pipe, tap } from 'ramda';
import { useMemo } from 'react';

const CUSTOM_OFFER_QUERY_STRING_PARAMETER = 'p';
const CUSTOM_OFFER_LOCAL_STORAGE_KEY = 'custom-offers';
const REACT_APP_QUERY_STRING_KEY = 'mnkn345';

export type Offer = {
  offerLine1: string;
  offerLine2: string;
};

export type CustomBonus = Pick<
  BonusI,
  | 'order'
  | 'title'
  | 'offer'
  | 'code'
  | 'description'
  | 'termsAndConditions'
  | 'background'
  | 'character'
  | 'buttonType'
  | 'buttonName'
  | 'buttonLink'
  | 'limited'
>;

export type CustomOffer = {
  bonuses?: CustomBonus[];
  ttl: number;
};

type CustomOfferStore = CustomOffer & {
  createAt: number;
};

const customOfferStore = new LocalStorage<CustomOfferStore, null>(
  CUSTOM_OFFER_LOCAL_STORAGE_KEY,
  null
);

const getCustomOfferFromLocalStorage = () => {
  const checkTTL = (customOffer: CustomOfferStore) =>
    new Date().getTime() - customOffer.createAt >= customOffer.ttl
      ? Maybe.None<CustomOfferStore>()
      : Maybe.Some(customOffer);

  return Maybe.fromNull(customOfferStore.get()).flatMap(checkTTL).orUndefined();
};

const getCustomOffers = (queryParam: string, decryptKey: string) => {
  const getQueryString = () =>
    parseFiltersFromQS(window.location.search, [queryParam]);

  const checkQueryParameters =
    (queryParam: string) =>
    (params: Partial<{ [x: string]: string | undefined }>) =>
      Maybe.fromNull(params[queryParam]);

  const decryptParameters = (decryptKey: string) => (cipherParams: string) =>
    Maybe.fromNull(decrypt<CustomOffer>(cipherParams, decryptKey));
  const saveParametersToLocalStorage = tap((customOffer: CustomOffer) =>
    customOfferStore.replace({ ...customOffer, createAt: new Date().getTime() })
  );

  const setOffersFromLocalStorage = () =>
    Maybe.fromNull(getCustomOfferFromLocalStorage());

  return pipe(getQueryString, (queryString) =>
    Maybe.fromNull(queryString)
      .flatMap(checkQueryParameters(queryParam))
      .flatMap(decryptParameters(decryptKey))
      .map(saveParametersToLocalStorage)
      .catchMap(setOffersFromLocalStorage)
      .orUndefined()
  )();
};

export const useCustomOffer = () =>
  useMemo(
    () =>
      getCustomOffers(
        CUSTOM_OFFER_QUERY_STRING_PARAMETER,
        REACT_APP_QUERY_STRING_KEY
      ),
    []
  );
